/**
 * PATH: packages/bills/src/components/SupplierPickerField.tsx
 * FILE_ID: BILLS-PKG-CMP-SUPPLIERPICKER-001
 * PURPOSE: Vendor-section control that links a bill to a canonical org
 *          Supplier (quotebuilder.Supplier) — the SAME list Xero contact
 *          sync, the email "Add as Supplier" action, and manual entry feed.
 * WHY: Operators asked "where is the Xero-synced supplier structure?" — bills
 *      only had a free-text vendor_name. This picker surfaces the structured
 *      supplier list (with provenance + Xero badge) and links the bill to it.
 *      2026-06: operators also hit the empty directory ("No suppliers found")
 *      and asked "if it didn't come up it should just create it". So the
 *      picker now (a) pre-seeds the search with the extracted vendor name so
 *      existing suppliers surface as recommendations on open, and (b) offers
 *      an inline "+ Create '<vendor>'" action when nothing matches.
 * UX: shows the linked supplier as a chip (with source badge); a search box
 *      opens a results list; picking sets the FK + fills vendor_name. Degrades
 *      to nothing when the app hasn't wired client.listSuppliers; the create
 *      affordance only shows when client.createSupplier is also wired.
 */
import { useEffect, useRef, useState } from 'react';
import { useBillsClient } from '../client';
import { supplierSourceLabel as sourceLabel } from '../supplierOption';
import {
  type PickerVendor,
  resolveCreateName,
  shouldOfferCreate,
  vendorContactForCreate,
} from '../supplierPicker';
import type { BillSupplierDetail, BillSupplierOption } from '../types';

interface Props {
  /** Currently-linked supplier id (from the draft/merged bill), or null. */
  selectedId: string | null;
  /** Server's read-only detail for the linked supplier (provenance/Xero). */
  detail: BillSupplierDetail | null;
  disabled?: boolean;
  /** Fired with the chosen supplier (or null to unlink). */
  onPick: (option: BillSupplierOption | null) => void;
  /**
   * The bill's extracted vendor. Drives two behaviours: the search box is
   * pre-seeded with `vendor.name` on open (so existing suppliers surface as
   * recommendations), and the inline create prefills email/ABN when the name
   * being created matches this vendor. Omitting it keeps the picker link-only.
   */
  vendor?: PickerVendor;
}

export function SupplierPickerField({ selectedId, detail, disabled, onPick, vendor }: Props) {
  const client = useBillsClient();
  const canList = typeof client.listSuppliers === 'function';
  const canCreate = typeof client.createSupplier === 'function';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<BillSupplierOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const vendorName = (vendor?.name ?? '').trim();

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Debounced fetch while the dropdown is open.
  useEffect(() => {
    if (!open || !canList) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const rows = await client.listSuppliers!(search.trim() || undefined);
        if (alive) setOptions(Array.isArray(rows) ? rows : []);
      } catch {
        if (alive) setOptions([]);
      } finally {
        if (alive) setLoading(false);
      }
    }, 200);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, search, canList, client]);

  const linkedLabel = detail?.name || null;
  // What the inline "create" action would name the new supplier, and whether
  // to surface it at all (hidden on an exact existing-name match → dedupe).
  const createName = resolveCreateName(search, vendorName);
  const offerCreate = shouldOfferCreate({ canCreate, loading, createName, options });

  function togglePicker() {
    const next = !open;
    setOpen(next);
    if (next) {
      // Seed the search with the extracted vendor name so matching suppliers
      // surface immediately (operators can clear it to browse the full list).
      if (!search.trim() && vendorName) setSearch(vendorName);
    } else {
      setCreateError(null);
    }
  }

  async function handleCreate() {
    if (!client.createSupplier || !createName) return;
    const contact = vendorContactForCreate(createName, vendor);
    setCreating(true);
    setCreateError(null);
    try {
      const created = await client.createSupplier({
        name: createName,
        email: contact.email,
        abn: contact.abn,
      });
      onPick(created);
      setOpen(false);
      setSearch('');
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Could not create supplier. Please try again.');
    } finally {
      setCreating(false);
    }
  }

  if (!canList) return null;

  return (
    <div ref={boxRef} className="relative text-xs">
      <span className="mb-1 block font-medium text-muted-foreground">Supplier (linked)</span>
      {selectedId && linkedLabel ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 py-1 text-sm">
          <span className="truncate font-medium">{linkedLabel}</span>
          {sourceLabel(detail?.source) && (
            <span
              className={
                'rounded-full px-1.5 py-0.5 text-[10px] font-medium ' +
                (detail?.source === 'xero'
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                  : 'bg-muted text-muted-foreground')
              }
              title={detail?.xero_contact_id ? `Xero contact ${detail.xero_contact_id}` : undefined}
            >
              {sourceLabel(detail?.source)}
            </span>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={() => onPick(null)}
              className="ml-auto text-[11px] text-muted-foreground hover:text-destructive"
              title="Unlink supplier"
            >
              × Unlink
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={togglePicker}
          className="block w-full min-w-0 truncate rounded-md border border-dashed border-border bg-background px-2 py-1.5 text-left text-xs text-muted-foreground hover:border-foreground/40 disabled:opacity-50"
        >
          + Link to a supplier…
        </button>
      )}

      {selectedId && linkedLabel && !disabled && (
        <button
          type="button"
          onClick={togglePicker}
          className="mt-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
        >
          Change supplier
        </button>
      )}

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <input
            autoFocus
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              if (createError) setCreateError(null);
            }}
            placeholder="Search suppliers (Xero, email, manual)…"
            className="w-full border-b border-border bg-background px-2 py-1.5 text-sm outline-none"
          />
          <ul className="max-h-60 overflow-y-auto py-1 text-sm">
            {loading && <li className="px-3 py-2 text-xs text-muted-foreground">Loading…</li>}
            {!loading &&
              options.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(o);
                      setOpen(false);
                      setSearch('');
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent hover:text-accent-foreground"
                  >
                    <span className="truncate">{o.name}</span>
                    {sourceLabel(o.source) && (
                      <span className="ml-auto rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {sourceLabel(o.source)}
                      </span>
                    )}
                  </button>
                </li>
              ))}

            {/* Inline create — operator-confirmed promotion of the free-text
                vendor into a canonical Supplier when nothing matches. */}
            {!loading && offerCreate && (
              <li className="border-t border-border/60">
                <button
                  type="button"
                  disabled={creating}
                  onClick={handleCreate}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  <span className="font-medium">
                    {creating ? 'Creating…' : `+ Create “${createName}” as a new supplier`}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {vendorContactForCreate(createName, vendor).email ||
                    vendorContactForCreate(createName, vendor).abn
                      ? 'Carries the vendor email / ABN from this bill, then links it.'
                      : 'Adds it to your supplier list, then links it to this bill.'}
                  </span>
                </button>
              </li>
            )}

            {!loading && options.length === 0 && !offerCreate && (
              <li className="px-3 py-2 text-xs text-muted-foreground">
                No suppliers found. Sync from Xero or add one from the email sender.
              </li>
            )}

            {!loading && options.length === 0 && offerCreate && (
              <li className="px-3 pb-2 pt-1 text-[11px] text-muted-foreground">
                …or sync from Xero / add from the email sender.
              </li>
            )}
          </ul>

          {createError && (
            <p className="border-t border-destructive/40 bg-destructive/5 px-3 py-1.5 text-[11px] text-destructive">
              {createError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
